<script setup lang="ts">
import { computed } from 'vue'
import plural from 'plural-ru'
import type { MarketProductCardItem } from '@/types/market'

const props = defineProps<{
  product: MarketProductCardItem
}>()

const isOwned = computed(() => props.product.is_owned && !props.product.is_creator)
const url = computed(() => `/market/view_product/${props.product.id}`)
const editUrl = computed(() => `/market/edit_product/${props.product.id}`)
const hasTeacherGrade = computed(() => {
  const user = (window as Window & { user?: { hasTeacherGrade?: boolean } }).user
  return Boolean(user?.hasTeacherGrade)
})

function reviewsText(count: number) {
  return `${count} ${plural(count, 'отзыв', 'отзыва', 'отзывов')}`
}
</script>

<template>
  <a
    class="product-card"
    :class="{
      _owned: isOwned,
      _disabled: !props.product.can_buy && !props.product.is_owned && !props.product.is_creator && hasTeacherGrade
    }"
    :href="url"
    target="_blank"
  >
    <ul class="product-card__badges">
      <li
        v-if="props.product.inspired_by_ace_state === 'y'"
        v-tooltip:bottom="'Товар проверен и одобрен методистом'"
        class="product-card__badge product-card__badge_ace-quality"
      >
        <v-icon class="mr-half">mdi-crown</v-icon>
        Качество АС
      </li>
    </ul>
    <v-img class="product-card__image" resize responsive :src="props.product.image" :width="304" :height="224"></v-img>
    <p class="product-card__type mt-2 mb-1">
      {{ props.product.content_types.map(c => c.name).join(', ') }}
    </p>
    <div class="product-card__info">
      <span class="mr-2">{{ reviewsText(props.product.review_count) }}</span>
      <v-rating :value="props.product.avg_rating"></v-rating>
    </div>

    <div class="product-card__name mt-1 mb-auto">
      <span>{{ props.product.name }}</span>
    </div>
    <div class="product-card__status">
      <div v-if="isOwned" class="product-card__owned mt-1 mx-1">
        <v-icon class="product-card__icon mr-half">mdi-check-bold</v-icon>
        Куплено
      </div>
      <div v-else class="product-card__price mt-1">
        <span>{{ props.product.price.toLocaleString() }}</span>
        <v-icon class="product-card__price-icon" size="24">hand</v-icon>
      </div>
      <div v-if="props.product.is_gift && isOwned" class="product-card__gift mt-1 mx-1">
        <v-icon class="product-card__icon mr-half">mdi-gift</v-icon>
        Подарок
      </div>
      <v-button
        v-if="props.product.is_creator"
        class="product-card__edit mt-1 ml-1"
        mode="primary small"
        :href="editUrl"
      >
        <v-icon class="product-card__icon mr-half" size="16">mdi-pencil</v-icon>
        Редактировать
      </v-button>
    </div>
  </a>
</template>

<style lang="scss">
.product-card {
  $root: &;

  display: flex;
  flex-direction: column;
  border-radius: $grid-gap;
  z-index: 0;
  position: relative;

  &:hover &__image::before {
    opacity: 0.3;
  }

  &__image {
    border: 1px solid rgba(25, 25, 25, 0.1);
    border-radius: 8px;

    &::before {
      content: '';
      position: absolute;
      background-color: $black;
      z-index: 1;
      opacity: 0;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      transition: opacity 0.3s;
    }
  }

  &__badges {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;

    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 8px;
  }

  &__badge {
    display: flex;
    align-items: center;
    gap: 4px;
    border-radius: 4px;
    padding: 0 4px;

    &_ace-quality {
      background: $orange;
      color: black;
      @include text-overline;
    }
  }

  &__info {
    @include text-caption;

    display: flex;
    align-items: center;
  }

  &__type {
    @include text-overline;

    margin-right: auto;
  }

  &__name {
    @include text-body-bold;
  }

  &__price {
    @include text-h3;

    display: flex;
    align-items: center;
  }

  &__status {
    @include text-title;

    display: flex;
    justify-content: space-between;
  }

  &__owned,
  &__gift {
    color: $green;
    font-weight: bold;
    display: flex;
    align-items: center;
  }

  &._owned {
    background-color: $lightenGreen;
  }

  &._disabled {
    opacity: 0.3;
  }
}
</style>
